import type {
  AllSelectionListsWithSkillTaxonomyKey,
  SelectionListKey,
} from '../../../shared/selection-lists/types';
import { useChart } from '../components/Chart/ChartProvider';
import type { PositionedItem } from '../components/PageConfigProvider/types/item';
import { usePageState } from '../components/PageState/PageStateProvider';
import { isSingleEntityChart } from '../components/PageState/utils/is-single-entity-chart';
import { usePrimaryFilterState } from '../filters/hooks/usePrimaryFilterState';
import {
  type Filter,
  FilterType,
  type PrimaryFilterId,
} from '../filters/types/filters';

export const useChartPrimaryFilter = ({
  primaryDimension,
}: {
  primaryDimension: PrimaryFilterId;
}): Record<AllSelectionListsWithSkillTaxonomyKey, number[] | undefined> => {
  const { primaryFilter } = usePrimaryFilterState(primaryDimension);

  const { item } = useChart();
  const { pageState } = usePageState();

  const primarySelections =
    primaryFilter?.selections?.map((selection) => ({
      [selection.levelId]: [Number(selection?.id?.split('_')?.slice(-1)?.[0])],
    })) ?? [];

  const levelIds = Array.from(
    new Set(primaryFilter?.selections?.map((selection) => selection.levelId))
  );

  const selectedEntity = pageState.primaryEntities?.[item?.id];
  const filteredPrimarySelections = selectedEntity
    ? primarySelections?.find((selection) =>
        selection?.[selectedEntity.levelId]?.includes(Number(selectedEntity.id))
      )
    : primarySelections?.[0];

  const undefinedLevelIds = levelIds.reduce<
    Record<AllSelectionListsWithSkillTaxonomyKey, undefined>
  >(
    (acc, levelId) => {
      acc[levelId as SelectionListKey] = undefined;
      return acc;
    },
    {} as Record<AllSelectionListsWithSkillTaxonomyKey, undefined>
  );

  return {
    ...undefinedLevelIds,
    ...filteredPrimarySelections,
  };
};

export const effectiveFiltersWithSinglePrimaryEntity = ({
  item,
  effectiveFilters,
  chartPrimaryFilter,
}: {
  item: PositionedItem;
  effectiveFilters: Filter[];
  chartPrimaryFilter: Record<
    AllSelectionListsWithSkillTaxonomyKey,
    number[] | undefined
  >;
}): Filter[] => {
  const chartHasSinglePrimaryEntity = isSingleEntityChart(item);

  if (!chartHasSinglePrimaryEntity) return effectiveFilters;

  const primaryFilter = effectiveFilters.find((filter) => filter.isPrimary);
  if (!primaryFilter || primaryFilter.type !== FilterType.TREE) {
    return effectiveFilters;
  }

  const primaryFilterSelections = primaryFilter.selections;

  const filteredPrimarySelections = primaryFilterSelections.filter(
    (selection) => {
      const chartPrimarySelection = chartPrimaryFilter[selection.levelId];
      if (!chartPrimarySelection) return false;
      return chartPrimarySelection.includes(
        Number(selection.id.split('_').pop())
      );
    }
  );

  return effectiveFilters.map((filter) => {
    if (filter.isPrimary) {
      return { ...filter, selections: filteredPrimarySelections };
    }
    return filter;
  });
};
